import type { ProgressionState } from '@/systems/ProgressionManager';
import { RobotDesign } from '@/scenes/WorkshopScene';

// ── Saved design with a user-given name (defined here to avoid circular deps) ──
export type DesignMode = 'campaign' | 'challenge';

export interface SavedDesign {
  name: string;
  design: RobotDesign;
  mode?: DesignMode;  // undefined treated as 'campaign' (backward compat)
}

const SLOT_PREFIX = 'robogame_slot_';
const META_KEY = 'robogame_slots_meta';
export const MAX_SLOTS = 3;

// ── Full slot data (progression + designs + scores) ──
export interface SlotData {
  progression: ProgressionState;
  designs: SavedDesign[];
  lastPlayed: string;  // ISO date string
  challengeCompletions?: Record<string, number>; // mapId → best stars (challenge mode)
}

// ── Slot summary for UI display (lightweight, no full data) ──
export interface SlotSummary {
  index: number;
  empty: boolean;
  lastPlayed: string;
  currency: number;
  levelsCompleted: number;
  componentsUnlocked: number;
  designCount: number;
}

interface SlotsMeta {
  activeSlot: number; // -1 = none
}

/**
 * Manages multiple save slots in localStorage.
 * Each slot stores progression + designs.
 * Settings (volume, difficulty, mute) remain global — not per-slot.
 */
class SlotManagerClass {
  private _activeSlot: number = -1;

  constructor() {
    this.loadMeta();
  }

  get activeSlot(): number { return this._activeSlot; }

  /** Get summary info for all slots (for UI display) */
  getSlotSummaries(): SlotSummary[] {
    const summaries: SlotSummary[] = [];
    for (let i = 0; i < MAX_SLOTS; i++) {
      const data = this.readSlot(i);
      if (!data) {
        summaries.push({
          index: i, empty: true, lastPlayed: '',
          currency: 0, levelsCompleted: 0, componentsUnlocked: 0, designCount: 0,
        });
      } else {
        summaries.push({
          index: i,
          empty: false,
          lastPlayed: data.lastPlayed,
          currency: data.progression.currency,
          levelsCompleted: (data.progression.completedIds ?? []).length,
          componentsUnlocked: (data.progression.unlockedComponents ?? []).length,
          designCount: (data.designs ?? []).length,
        });
      }
    }
    return summaries;
  }

  /** Read raw slot data (null if empty) */
  readSlot(index: number): SlotData | null {
    try {
      const raw = localStorage.getItem(SLOT_PREFIX + index);
      if (!raw) return null;
      return JSON.parse(raw) as SlotData;
    } catch (e) { console.warn('[SlotManager] readSlot failed:', e); return null; }
  }

  /** Write slot data */
  writeSlot(index: number, data: SlotData): void {
    try {
      data.lastPlayed = new Date().toISOString();
      localStorage.setItem(SLOT_PREFIX + index, JSON.stringify(data));
    } catch (e) { console.warn('[SlotManager] writeSlot failed:', e); }
  }

  /** Delete a slot */
  deleteSlot(index: number): void {
    try {
      localStorage.removeItem(SLOT_PREFIX + index);
      if (this._activeSlot === index) {
        this._activeSlot = -1;
        this.saveMeta();
      }
    } catch (e) { console.warn('[SlotManager] deleteSlot failed:', e); }
  }

  /** Set the active slot index */
  setActiveSlot(index: number): void {
    this._activeSlot = index;
    this.saveMeta();
  }

  /** Save current game state to the active slot */
  saveToActiveSlot(progression: ProgressionState, designs: SavedDesign[]): void {
    if (this._activeSlot < 0) return;
    this.writeSlot(this._activeSlot, {
      progression,
      designs,
      lastPlayed: new Date().toISOString(),
    });
  }

  /** Update only the progression part of the active slot */
  updateProgression(progression: ProgressionState): void {
    if (this._activeSlot < 0) return;
    const data = this.readSlot(this._activeSlot);
    if (data) {
      data.progression = progression;
      this.writeSlot(this._activeSlot, data);
    } else {
      this.writeSlot(this._activeSlot, { progression, designs: [], lastPlayed: '' });
    }
  }

  /** Update challenge completions in the active slot */
  updateChallengeCompletions(completions: Record<string, number>): void {
    if (this._activeSlot < 0) return;
    const data = this.readSlot(this._activeSlot);
    if (data) {
      data.challengeCompletions = completions;
      this.writeSlot(this._activeSlot, data);
    }
  }

  /** Read challenge completions from the active slot */
  getChallengeCompletions(): Record<string, number> {
    if (this._activeSlot < 0) return {};
    const data = this.readSlot(this._activeSlot);
    return data?.challengeCompletions ?? {};
  }

  /** Update only the designs part of the active slot */
  updateDesigns(designs: SavedDesign[]): void {
    if (this._activeSlot < 0) return;
    const data = this.readSlot(this._activeSlot);
    if (data) {
      data.designs = designs;
      this.writeSlot(this._activeSlot, data);
    }
    // If no slot data exists yet, skip — progression must be initialized first
  }

  /** Check if any slot has data */
  hasAnySave(): boolean {
    for (let i = 0; i < MAX_SLOTS; i++) {
      if (this.readSlot(i) !== null) return true;
    }
    return false;
  }

  // ── Migration: import old single-save into slot 0 if no slots exist ──
  migrateOldSave(): void {
    if (this.hasAnySave()) return;
    try {
      const oldProg = localStorage.getItem('robogame_progression');
      const oldSave = localStorage.getItem('robogame_save');
      if (!oldProg) return;

      const progression: ProgressionState = JSON.parse(oldProg);
      const saveData = oldSave ? JSON.parse(oldSave) : {};
      const designs: SavedDesign[] = saveData.designs ?? [];

      this.writeSlot(0, {
        progression,
        designs,
        lastPlayed: new Date().toISOString(),
      });
      this._activeSlot = 0;
      this.saveMeta();
      // Remove old keys so migration doesn't re-trigger after slot deletion
      localStorage.removeItem('robogame_progression');
      localStorage.removeItem('robogame_save');
    } catch (e) { console.warn('[SlotManager] migrateOldSave failed:', e); }
  }

  private loadMeta(): void {
    try {
      const raw = localStorage.getItem(META_KEY);
      if (raw) {
        const meta: SlotsMeta = JSON.parse(raw);
        this._activeSlot = meta.activeSlot ?? -1;
      }
    } catch (e) { console.warn('[SlotManager] loadMeta failed:', e); this._activeSlot = -1; }
  }

  private saveMeta(): void {
    try {
      localStorage.setItem(META_KEY, JSON.stringify({ activeSlot: this._activeSlot }));
    } catch (e) { console.warn('[SlotManager] saveMeta failed:', e); }
  }
}

export const SlotManager = new SlotManagerClass();
